import React from "react";
import { CheckCircle2, ShieldCheck, Sparkles, Star } from "lucide-react";
import Mascot from "@/components/Mascot";

export default function Hero() {
  return (
    <section
      id="top"
      className="relative overflow-hidden hero-grad text-white"
      data-testid="hero-section"
    >
      {/* decorative bubbles */}
      <div className="bubble" style={{ width: 110, height: 110, top: 60, left: 40 }} />
      <div className="bubble" style={{ width: 60, height: 60, top: 180, left: 200 }} />
      <div className="bubble" style={{ width: 36, height: 36, top: 100, right: 120 }} />
      <div className="bubble" style={{ width: 80, height: 80, bottom: 80, right: 60 }} />
      <div className="bubble" style={{ width: 24, height: 24, bottom: 140, left: 60 }} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 md:pt-24 pb-20 md:pb-32 grid lg:grid-cols-12 gap-10 items-center relative">
        <div className="lg:col-span-7">
          <div className="inline-flex items-center gap-2 bg-white/15 border border-white/25 backdrop-blur-md rounded-full pl-2 pr-4 py-1.5 mb-6">
            <span className="bg-[var(--fc-orange)] text-white text-[11px] font-extrabold uppercase tracking-wider px-2.5 py-1 rounded-full">
              St. Cloud, FL
            </span>
            <span className="text-white/90 text-sm font-bold">
              Serving all of Central Florida · Licensed &amp; Insured
            </span>
          </div>

          <div className="mb-4" data-testid="hero-brand-name">
            <div className="flex items-center justify-start gap-3 sm:gap-4">
              <div
                className="font-script font-bold text-6xl sm:text-7xl lg:text-8xl text-white leading-none drop-shadow-[0_6px_0_rgba(11,47,108,0.9)] [text-shadow:_0_2px_0_#0B2F6C,_0_4px_18px_rgba(0,0,0,0.35)]"
                style={{ WebkitTextStroke: "2px #0B2F6C" }}
              >
                Faithfully Clean
              </div>
              <svg
                viewBox="0 0 64 32"
                className="h-9 sm:h-12 w-auto text-[var(--fc-yellow)] drop-shadow-[0_2px_3px_rgba(0,0,0,0.45)]"
                aria-label="Ichthys fish symbol"
                data-testid="ichthys-icon"
              >
                {/* Classic Ichthys: two crossing curves with a small eye */}
                <path
                  d="M 6 6 Q 32 36 58 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3.2"
                  strokeLinecap="round"
                />
                <path
                  d="M 6 26 Q 32 -4 58 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3.2"
                  strokeLinecap="round"
                />
                <circle cx="50" cy="14" r="1.4" fill="currentColor" />
              </svg>
            </div>
            <div className="mt-2 text-[var(--fc-yellow)] text-sm sm:text-base font-extrabold uppercase tracking-[0.22em] drop-shadow-[0_2px_4px_rgba(0,0,0,0.4)]">
              Pressure Washing &amp; Exterior Cleaning
            </div>
          </div>

          <h1 className="font-heading font-bold leading-[0.95] text-5xl sm:text-6xl lg:text-7xl">
            <span className="block text-white">FROM GRIME</span>
            <span
              className="block text-[var(--fc-orange)]"
              style={{ WebkitTextStroke: "1.5px rgba(0,0,0,0.15)" }}
            >
              TO GLORY
            </span>
            <span className="block text-white text-3xl sm:text-4xl lg:text-5xl mt-3">
              We Wash It <span className="font-script text-[var(--fc-yellow)] text-5xl sm:text-6xl">All</span>
            </span>
          </h1>

          <p className="mt-6 max-w-xl text-lg md:text-xl text-white/85 leading-relaxed">
            Faithfully Clean restores homes &amp; businesses across Central Florida — based in St. Cloud,
            soft-wash safe, faith-anchored, and finished with care.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-3" data-testid="hero-cta-group">
            <a
              href="#estimate"
              className="inline-flex items-center justify-center btn-orange font-heading font-extrabold uppercase tracking-wider px-7 h-14 rounded-full text-base sm:text-lg transition-all"
              data-testid="hero-cta-estimate"
            >
              Get a FREE Estimate
            </a>
            <a
              href="tel:4073752862"
              className="inline-flex items-center justify-center bg-white text-[var(--fc-blue-dark)] font-heading font-extrabold uppercase tracking-wider px-7 h-14 rounded-full text-base sm:text-lg hover:bg-slate-100 transition-all"
              data-testid="hero-cta-call"
            >
              Call 407-375-2862
            </a>
          </div>

          <div className="mt-10 flex flex-wrap gap-x-6 gap-y-3 text-sm font-bold">
            {[
              { icon: CheckCircle2, label: "Free Estimates" },
              { icon: ShieldCheck, label: "Licensed & Insured" },
              { icon: Sparkles, label: "Mold, Mildew & Algae Removal" },
              { icon: Star, label: "100% Satisfaction" },
            ].map((b) => (
              <div key={b.label} className="flex items-center gap-2 text-white/95">
                <b.icon className="w-5 h-5 text-[var(--fc-yellow)] sparkle" />
                {b.label}
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-5 relative flex justify-center" data-testid="hero-mascot-wrap">
          <div className="floaty relative">
            <Mascot className="w-[300px] sm:w-[400px] lg:w-[480px] h-auto drop-shadow-[0_22px_30px_rgba(0,0,0,0.55)]" />
          </div>
          {/* sparkles */}
          <Sparkles className="absolute top-2 right-6 w-8 h-8 text-[var(--fc-yellow)] sparkle" />
          <Sparkles className="absolute bottom-8 left-2 w-6 h-6 text-white sparkle" />
        </div>
      </div>

      {/* wave bottom */}
      <svg className="wave-divider absolute -bottom-1 left-0" viewBox="0 0 1440 80" preserveAspectRatio="none" aria-hidden="true">
        <path d="M0,40 C240,80 480,0 720,30 C960,60 1200,20 1440,50 L1440,80 L0,80 Z" fill="#ffffff" />
      </svg>
    </section>
  );
}
